import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import { postAgentService, postService } from "../services";
import {
  getPortal,
  getToken,
  removePortal,
  storePortal,
  storeToken,
} from "../../Data/services/localStorageService";

/** Aligns with Eternal Tattoo admin login `data` object (Postman). */
const initialUserInfo = {
  id: 0,
  name: "",
  firstname: "",
  lastname: "",
  email: "",
  username: "",
  profile_image: "",
  image_url: "",
  role: "",
  user_type: "",
  api_token: getToken() || "",
  is_activated: false,
  is_blocked: false,
};

const initialState = {
  accessToken: "",
  isSocketConnected: false,
  loading: false,
  forgotPasswordLoading: false,
  changePasswordLoading: false,
  portal: getPortal(),
  userInfo: { ...initialUserInfo },
};

function normalizeLoginResponse(envelope, response, rejectWithValue) {
  const code = envelope.code ?? envelope.status;
  if (typeof code === "number" && code !== 200) {
    return rejectWithValue({
      httpStatus: response.status,
      body: envelope,
    });
  }

  const inner = envelope.data ?? envelope;
  if (inner != null && typeof inner.status === "number" && inner.status >= 400) {
    return rejectWithValue({
      httpStatus: response.status,
      body: inner,
    });
  }

  if (!inner?.api_token) {
    return rejectWithValue({
      httpStatus: response.status,
      body: {
        message: envelope.message || "Login failed: missing api_token",
        data: inner,
      },
    });
  }

  storeToken(inner.api_token);
  return inner;
}

export const login = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    const loginAs = data.loginAs === "agent" ? "agent" : "admin";
    const { loginAs: _omitLoginAs, ...body } = data;

    try {
      const response =
        loginAs === "agent"
          ? await postAgentService(api_urls.agentLogin, body)
          : await postService(api_urls.login, body);
      const envelope = response.data ?? {};
      return normalizeLoginResponse(envelope, response, rejectWithValue);
    } catch (error) {
      console.error("error from login API =====>", error);
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (
    { current_password, new_password, confirm_password },
    { getState, rejectWithValue }
  ) => {
    const useAgent = getState().auth?.portal === "agent";
    const body = { current_password, new_password, confirm_password };
    try {
      const response = useAgent
        ? await postAgentService(api_urls.changePassword, body)
        : await postService(api_urls.changePassword, body);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email, loginAs }, { rejectWithValue }) => {
    const portal = loginAs === "agent" ? "agent" : "admin";
    const payload = { email };

    try {
      const response =
        portal === "agent"
          ? await postAgentService(api_urls.agentForgotPassword, payload)
          : await postService(api_urls.forgotPassword, payload);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      return envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    saveAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    removeAccessToken: (state) => {
      state.accessToken = "";
      state.userInfo = { ...initialUserInfo, api_token: "" };
      state.isSocketConnected = false;
      state.portal = null;
      removePortal();
    },
    saveUserInfo: (state, action) => {
      state.userInfo = { ...state.userInfo, ...action.payload };
    },
    clearUserInfo: (state) => {
      state.userInfo = { ...initialUserInfo };
      state.portal = null;
    },
    socketConnected: (state, actions) => {
      return {
        ...state,
        isSocketConnected: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, { payload, meta }) => {
        state.loading = false;
        const loginAs = meta.arg?.loginAs === "agent" ? "agent" : "admin";
        state.portal = loginAs;
        storePortal(loginAs);
        if (payload?.api_token) {
          state.accessToken = `Bearer ${payload.api_token}`;
        }
        state.userInfo = {
          ...state.userInfo,
          ...payload,
          role: payload?.role || payload?.user_type || state.userInfo.role,
        };
      })
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordLoading = true;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordLoading = false;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.forgotPasswordLoading = false;
      })
      .addCase(changePassword.pending, (state) => {
        state.changePasswordLoading = true;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.changePasswordLoading = false;
      })
      .addCase(changePassword.rejected, (state) => {
        state.changePasswordLoading = false;
      });
  },
});

export const { saveAccessToken, removeAccessToken, saveUserInfo, clearUserInfo ,socketConnected} =
  authSlice.actions;

export default authSlice.reducer;
